import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';
import { normalizeOrganizationSlug } from '@/lib/slugUtils';
import { OrganizationRegistry } from './types';
import { getRegistryConfig } from '@/services/supabase/config';
import { createSupabaseClient } from './utils/clientFactory';

function normalizeSlug(slug: string): string {
  return normalizeOrganizationSlug(slug) || slug.trim().toLowerCase();
}

function mapRegistryRow(data: {
  id: string;
  organizationSlug: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  isActive: boolean;
}): OrganizationRegistry {
  return {
    id: data.id,
    organizationSlug: data.organizationSlug,
    supabaseUrl: data.supabaseUrl,
    supabaseAnonKey: data.supabaseAnonKey,
    isActive: data.isActive,
  };
}

function isValidRegistryEdgeResponse(data: unknown): data is {
  id: string;
  organizationSlug: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  isActive: boolean;
} {
  if (!data || typeof data !== 'object') return false;
  const row = data as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.organizationSlug === 'string' &&
    typeof row.supabaseUrl === 'string' &&
    typeof row.supabaseAnonKey === 'string' &&
    typeof row.isActive === 'boolean'
  );
}

/**
 * Registry Client
 * Manages connection to the central organization registry database.
 * Org lookup/register go through registry Edge Functions (service_role).
 */
export class RegistryClient {
  private client: SupabaseClient;
  private registryCache = new Map<string, OrganizationRegistry>();
  private inFlightLookups = new Map<string, Promise<OrganizationRegistry | null>>();

  constructor() {
    const config = getRegistryConfig();
    this.client = createSupabaseClient(config);
  }

  /**
   * Gets the Supabase client instance from the Registry
   */
  getClient(): SupabaseClient {
    return this.client;
  }

  /**
   * Gets the organization information by its slug
   */
  async getOrganizationBySlug(slug: string): Promise<OrganizationRegistry | null> {
    const normalizedSlug = normalizeSlug(slug);
    if (!normalizedSlug) return null;

    const cached = this.registryCache.get(normalizedSlug);
    if (cached) return cached;

    const inFlight = this.inFlightLookups.get(normalizedSlug);
    if (inFlight) return inFlight;

    const lookup = this.resolveOrganizationViaEdgeFunction(normalizedSlug).finally(() => {
      this.inFlightLookups.delete(normalizedSlug);
    });
    this.inFlightLookups.set(normalizedSlug, lookup);

    const result = await lookup;
    if (result) {
      this.registryCache.set(normalizedSlug, result);
    }
    return result;
  }

  private async resolveOrganizationViaEdgeFunction(
    normalizedSlug: string,
  ): Promise<OrganizationRegistry | null> {
    const { data, error } = await this.client.functions.invoke('resolve-organization', {
      body: { organizationSlug: normalizedSlug },
    });

    if (error) {
      if (error instanceof FunctionsHttpError && error.context.status === 404) {
        return null;
      }
      const payload = data as { error?: string } | null;
      throw new Error(typeof payload?.error === 'string' ? payload.error : error.message);
    }

    if (!isValidRegistryEdgeResponse(data)) {
      throw new Error('invalid_registry_response');
    }

    return mapRegistryRow(data);
  }

  /**
   * Registers a new organization in the registry
   */
  async registerOrganization(
    slug: string,
    supabaseUrl: string,
    supabaseAnonKey: string,
  ): Promise<boolean> {
    const normalizedSlug = normalizeSlug(slug);
    if (!normalizedSlug) return false;

    const trimmedUrl = supabaseUrl.trim().replace(/\/$/, '');
    const trimmedAnonKey = supabaseAnonKey.trim();

    const { data, error } = await this.client.functions.invoke('register-organization', {
      body: {
        organizationSlug: normalizedSlug,
        supabaseUrl: trimmedUrl,
        supabaseAnonKey: trimmedAnonKey,
      },
    });

    if (error) {
      const payload = data as { error?: string } | null;
      throw new Error(typeof payload?.error === 'string' ? payload.error : error.message);
    }

    const result = data as { registered?: boolean };
    const registered = result.registered === true;
    if (registered) this.registryCache.delete(normalizedSlug);
    return registered;
  }
}
